import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { nombreDePages } from '@/shared/api/troncature';

/**
 * La barre de pagination partagee par les ecrans de liste.
 *
 * ELLE AFFICHE LE TOTAL REEL, et c'est sa raison d'etre principale. Avant ce lot,
 * les ecrans ecrivaient `assets.length` : un compte qui plafonnait a la borne sans
 * le dire. « 500 resultats » pouvait vouloir dire 500, ou 6 812.
 *
 * CIBLES TACTILES : hauteur 44 px minimum (h-11). C'est la norme retenue pour les
 * ecrans de bureau consultes en mobile ; le parcours poseur, ou l'on travaille debout
 * chez un client, reste a 56 px. Une barre de pagination a 32 px se rate au doigt, et
 * un rate ici change de page — donc fait perdre sa place a l'utilisateur.
 */
export default function BarrePagination({ etat, onPage, quoi = 'resultats', busy }) {
  const total = etat?.total;
  if (total == null) return null;

  const pages = nombreDePages(etat);
  const page = etat.page || 1;
  // Une seule page ET aucun element : l'ecran affiche deja son propre message de
  // liste vide, une barre « 0 resultat, page 1 sur 1 » ne dirait rien de plus.
  if (pages <= 1 && total === 0) return null;

  const aller = (n) => !busy && onPage(Math.min(Math.max(n, 1), pages));
  const bouton = 'h-11 min-w-11 px-3';

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 pt-1"
      data-testid="barre-pagination"
    >
      <p className="text-[12px] text-neutral-600" data-testid="pagination-total">
        <span className="font-semibold text-neutral-900">
          {total.toLocaleString('fr-FR')}
        </span>{' '}
        {quoi}
        {pages > 1 && (
          <span className="text-neutral-400">
            {' '}· {etat.taille} par page
          </span>
        )}
      </p>

      {pages > 1 && (
        <div className="flex items-center gap-1">
          <Button variant="outline" className={bouton} disabled={page <= 1}
                  onClick={() => aller(1)} aria-label="Premiere page"
                  data-testid="pagination-premiere">
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" className={bouton} disabled={page <= 1}
                  onClick={() => aller(page - 1)} aria-label="Page precedente"
                  data-testid="pagination-precedent">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-2 text-[12px] text-neutral-700 whitespace-nowrap"
                data-testid="pagination-position">
            Page {page} sur {pages}
          </span>
          <Button variant="outline" className={bouton} disabled={page >= pages}
                  onClick={() => aller(page + 1)} aria-label="Page suivante"
                  data-testid="pagination-suivant">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" className={bouton} disabled={page >= pages}
                  onClick={() => aller(pages)} aria-label="Derniere page"
                  data-testid="pagination-derniere">
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
